import { Anchor, Button } from "@newsela/angelou";
import {
  withTrackEvent,
  actionNames,
  objectTypes,
  actionPrefixes,
} from "@newsela/snitch";

import {
  formCardContainerCss,
  completePercentageBarCss,
  cardTitleCss,
  anchorCss,
  incompleteFillPercentageCss,
  completePercentageCss,
  percentageBarSharedCss,
  percentageContainer,
  incompletePercentageBarCss,
  percentageCopyCss,
} from "./FormCard.style";

interface FormCardProps {
  cardUrl: string;
  formTitle: string;
  completionRate: number;
  numberOfIncompleteQuestions: number;
  hasErrors: boolean;
}

type AnchorProps = React.ComponentProps<typeof Anchor>;

const AnchorWithTrackEvent = withTrackEvent(
  Anchor,
) as React.ForwardRefExoticComponent<
  AnchorProps & React.RefAttributes<HTMLAnchorElement>
>;

const ButtonWithTrackEvent = withTrackEvent(Button);

const FormCard = ({
  cardUrl,
  formTitle,
  completionRate,
  numberOfIncompleteQuestions,
  hasErrors,
}: FormCardProps) => {
  const percentageOfCompletedQuestions = Math.round(completionRate * 100);

  const remainingQuestionsButtonText = () => {
    if (numberOfIncompleteQuestions < 0) {
      return "Answer questions";
    }
    if (numberOfIncompleteQuestions === 0) {
      return "Review and edit";
    }
    if (numberOfIncompleteQuestions === 1) {
      return `Answer ${numberOfIncompleteQuestions} question`;
    } else {
      return `Answer ${numberOfIncompleteQuestions} questions`;
    }
  };

  const completedQuestionsPercentageBar = () => {
    if (percentageOfCompletedQuestions < 0) {
      return (
        <div css={[percentageBarSharedCss, incompletePercentageBarCss]}>
          <div css={percentageCopyCss}>% Unavailable</div>
        </div>
      );
    }
    if (
      percentageOfCompletedQuestions < 100 &&
      percentageOfCompletedQuestions !== 0
    ) {
      return (
        <div css={[percentageBarSharedCss, incompletePercentageBarCss]}>
          <div
            css={incompleteFillPercentageCss(percentageOfCompletedQuestions)}
          />
          <div css={percentageCopyCss}>
            {" "}
            {percentageOfCompletedQuestions}% Complete{" "}
          </div>
        </div>
      );
    }
    if (percentageOfCompletedQuestions === 100) {
      return (
        <div css={[percentageBarSharedCss, completePercentageCss]}>
          {" "}
          {percentageOfCompletedQuestions}% Completed
        </div>
      );
    } else {
      return (
        <div css={[percentageBarSharedCss, completePercentageBarCss]}>
          {" "}
          {percentageOfCompletedQuestions}% Completed
        </div>
      );
    }
  };

  const ctaText = remainingQuestionsButtonText();

  const sharedCTAProps = {
    objectType: objectTypes.user,
    actionName: actionNames.navigate,
    actionPrefix: actionPrefixes.initiate,
    dataProps: { "data-qa-selector": `${ctaText} ${formTitle} button` },
  };

  return (
    <div css={formCardContainerCss}>
      <div>
        <div css={cardTitleCss}>{formTitle}</div>
        <div css={percentageContainer}>{completedQuestionsPercentageBar()}</div>
      </div>
      <div css={anchorCss}>
        {hasErrors ? (
          <ButtonWithTrackEvent
            disabled
            flavor={Button.flavors.secondary}
            size={Button.sizes.md}
            {...sharedCTAProps}
          >
            {ctaText}
          </ButtonWithTrackEvent>
        ) : (
          <AnchorWithTrackEvent
            href={cardUrl}
            styleAnchorAsButton={{
              flavor: Button.flavors.secondary,
              size: Button.sizes.md,
            }}
            {...sharedCTAProps}
          >
            {ctaText}
          </AnchorWithTrackEvent>
        )}
      </div>
    </div>
  );
};

export default FormCard;
